
  import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
  import {
    FridgeDetailsMylist
  } from "@/types/fridgeDetails";
  import NativeClient, { TitleInfo } from "@/client/native-client";
  import store from "@/store";
  import router from "@/router";
  import { useApiErrorHandler } from "@/use/use-api-error-handler";
  import { useApiErrorDialog } from "@/use/use-api-error-dialog";
  import { useLog } from "@/use/use-log";
  import { PAGE_ID } from "@/const/log-const";
  import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
  import { COMMON_LIMIT_NUM } from "@/const/limit_const"; 
  import { COMMON_MESSAGE_STRING } from "@/const/message_const";
  import CommonUtil, { translateYYYYMMDDToJapan } from "@/utils/common-utils";
  import { translateFridgeDetailsSettingsToBuffer } from "@/utils/fridgeDetails-utils";
  import FridgeDetailsMyAssistList from "@/components/fridgeDetails/FridgeDetailsMyAssistList.vue";
  import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
  
  export default defineComponent({
    name: "FridgeMyAssistListEditPage",
    components: { FridgeDetailsMyAssistList, CommonDesignDialog },
    setup() {
      const { setLogId, outputPageLogs } = useLog();
  
      // タイトル行の設定
      NativeClient.setTitle(new TitleInfo(false, "編集するマイリストを選択"));
      CommonUtil.scroll({ top: 0, left: 0 });

      // 定数
      const isEditMode = true;

      // ダイアログ情報 API実行エラーのダイアログは、割り込まれるので、API実行前に、以下の情報で判別する
      class FridgeCoolingAssistMyAssistEditDialogInfo {
        type: number;         // 0:なし、1:マイアシスト取得、2:機器制御実行API実行
        negativeCallback: (() => void) | null;
        positiveCallback: (() => void) | null;
        constructor(type: number, negativeCallback: (() => void) | null, positiveCallback: (() => void) | null) {
          this.type = type;
          this.negativeCallback = negativeCallback;
          this.positiveCallback = positiveCallback;
        }
      } 
      const refDialogInfo = ref(new FridgeCoolingAssistMyAssistEditDialogInfo(0, null, null));
  
      // ダイアログ
      const isShowDialog = ref(false);
      const dialogType = ref(0);
      const dialogTitle = ref("");
      const dialogMessage = ref("");
      const dialogPositiveButtonLabel = ref("");
      const dialogNegativeButtonLabel = ref("");
      const dialogPositiveHandler = () => {
        isShowDialog.value = false;
        if (refDialogInfo.value.positiveCallback != null) {
          refDialogInfo.value.positiveCallback();
        }
      };
      const dialogNegativeHandler = () => {
        isShowDialog.value = false;
        if (refDialogInfo.value.negativeCallback != null) {
          refDialogInfo.value.negativeCallback();
        }
      };
      const dialogCloseHandler = () => {
        isShowDialog.value = false;
      };
  
      // 表示更新のためのリアクティブ
      const updateDisplayKey = ref(0);
      const updateDisplay = () => {
        if (updateDisplayKey.value == 100) {
          updateDisplayKey.value = 0;
        } else {
          updateDisplayKey.value++;
        }
      };
  
      // マイリスト
      const myAssistList = computed(() => {
        NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistList()");
        const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
        if (data == null) {
          return null;
        }
        NativeClient.sendDebugLog("FridgeCoolingAssistPage myAssistList() list = " + JSON.stringify(data.mylist_list));
        return data.mylist_list;
      });
  
      //
      // API実行
      //
      let isDeletingComplete = false;
      const refAPIDialog = ref(new FridgeCoolingAssistMyAssistEditDialogInfo(0, null, null));
      const getData = async () => {
        NativeClient.sendDebugLog("FridgeCoolingAssist getData()");
        refAPIDialog.value = new FridgeCoolingAssistMyAssistEditDialogInfo(1, null, getData);
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        await store.dispatch("fridgeDetails/getMyAssist")
        store.dispatch("common/stopLoading");
        updateDisplay();

        const data: FridgeDetailsMylist | null = store.getters["fridgeDetails/getMyAssist"];
        if ((data != null && data.mylist_list.length == 0) && isDeletingComplete) {
          router.back();
        }
      };
      let deleteId = 0;
      const deleteItemBody = async () => {
        NativeClient.sendDebugLog("FridgeCoolingAssist deleteItem()");
        refAPIDialog.value = new FridgeCoolingAssistMyAssistEditDialogInfo(2, null, deleteItemBody);
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        await store.dispatch("fridgeDetails/deleteMyAssist", deleteId),
        store.dispatch("common/stopLoading");
        refAPIDialog.value = new FridgeCoolingAssistMyAssistEditDialogInfo(0, null, null);
        isDeletingComplete = true;

        getData();
      };
      const deleteItem = async (id: number) => {
        deleteId = id;
        deleteItemBody();
      };

      // 表示の制御
      onMounted(() => {
        NativeClient.sendDebugLog("FridgeCoolingAssistPage onMounted()");
        getData();
      });
  
      //
      // ボタン類
      //
      // マイアシスト項目選択
      const clickMyAssistItem = (id: number) => {
        NativeClient.sendDebugLog("FridgeCoolingAssistPage clickMyAssistItem() id = " + id);
        router.push(`/fridgeMyAssistEditing?id=${id}`);
      };
      // マイリスト削除
      let deleteTargetId = 0;
      const deleteAction = () => {
        deleteItem(deleteTargetId);
      };
      const clickDeleteMyAssistItem = (id: number, name: string) => {
        NativeClient.sendDebugLog("FridgeCoolingAssistPage clickDeleteMyAssistItem(id:" + id + ", name: " + name + ")");
        deleteTargetId = id;
        dialogType.value = 18;
        dialogTitle.value = "";
        dialogMessage.value = `${name}を削除しますか？`
        dialogPositiveButtonLabel.value = "削除";
        dialogNegativeButtonLabel.value = "キャンセル"; 
        refDialogInfo.value = new FridgeCoolingAssistMyAssistEditDialogInfo(18, null, deleteAction)
        isShowDialog.value = true;
      };
  
      //
      // APIリクエスト時のエラーハンドル
      //
      const {
        handleApiError,
        getApiErrorTitle,
        getApiErrorMessage
      } = useApiErrorHandler();
      const { closeDialog } = useApiErrorDialog();
      const typeApiError = ref(9);
      const retryButtonTitle = "リトライ";
      const okButtonTitle = "OK";
      // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
      const retryApiErrorHandler = () => {
        closeDialog();
        // if (refAPIDialog.value.positiveCallback != null) {
        //   refAPIDialog.value.positiveCallback();
        // }
      };
      const okApiErrorHandler = () => {
        closeDialog();
      };
  
      return {
        updateDisplayKey,
        myAssistList,
        isEditMode,
        clickMyAssistItem,
        clickDeleteMyAssistItem,
        handleApiError,
        getApiErrorTitle,
        getApiErrorMessage,
        typeApiError,
        retryButtonTitle,
        okButtonTitle,
        retryApiErrorHandler,
        okApiErrorHandler,
        isShowDialog,
        dialogType,
        dialogTitle,
        dialogMessage,
        dialogPositiveButtonLabel,
        dialogNegativeButtonLabel,
        dialogPositiveHandler,
        dialogNegativeHandler,
        dialogCloseHandler
      };
    }
  });
  